import { useCallback, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import Iconify from "../../../components/iconify";
import {
  computeGrossPrice,
  formatPromotionValidUntil,
  getActivePromotion,
  getProductCategoryLabels,
  resolveProductImageUrl,
  toCurrency,
} from "./shopUtils";

// Drag handle + droppable product row
function DraggableProductRow({
  product,
  onEdit,
  onDelete,
  onToggleActive,
  deletingProduct,
  togglingId,
}) {
  const productId = String(product._id);
  const categoryLabels = getProductCategoryLabels(product);
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({ id: productId, data: { product } });
  const { isOver, setNodeRef: setDropRef } = useDroppable({ id: productId });

  const transformStyle = transform ? CSS.Translate.toString(transform) : null;
  const activeVariants = (product.variants || []).filter(
    (v) => v.isActive !== false
  );
  const activePromotion = getActivePromotion(product);
  const promotionUntilText = activePromotion
    ? formatPromotionValidUntil(activePromotion.validUntil)
    : "";

  return (
    <Grid item xs={12} md={6}>
      <Paper
        ref={(node) => {
          setDragRef(node);
          setDropRef(node);
        }}
        variant="outlined"
        sx={{
          p: 2,
          opacity: isDragging ? 0.3 : 1,
          transform: transformStyle,
          borderColor: isOver ? "primary.main" : "divider",
          borderWidth: isOver ? 2 : 1,
          transition: "border-color 0.15s, opacity 0.15s",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Drag handle */}
            <Box
              {...listeners}
              {...attributes}
              sx={{
                cursor: "grab",
                display: "flex",
                alignItems: "center",
                color: "text.disabled",
                "&:hover": { color: "text.primary" },
                flexShrink: 0,
              }}
            >
              <Iconify icon="mdi:drag-vertical" width={22} />
            </Box>

            {/* Thumbnail */}
            {product.images?.[0] ? (
              <Box
                component="img"
                src={resolveProductImageUrl(product.images[0])}
                alt={product.name}
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: "cover",
                  borderRadius: 1,
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Iconify
                  icon="eva:image-outline"
                  width={24}
                  sx={{ color: "grey.400" }}
                />
              </Box>
            )}

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1" noWrap>
                  {product.name}
                </Typography>
                <Switch
                  size="small"
                  checked={Boolean(product.isActive)}
                  onChange={() => onToggleActive(product)}
                  disabled={togglingId === product._id}
                />
              </Stack>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.5 }}
              >
                {categoryLabels.map((category) => (
                  <Chip
                    key={category}
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={category}
                    sx={{
                      maxWidth: 160,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                      },
                    }}
                  />
                ))}
                {activeVariants.length > 0 ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${activeVariants.length} Var.`}
                  />
                ) : null}
                {activePromotion ? (
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label={`-${activePromotion.discountPercent}% bis ${promotionUntilText}`}
                  />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary" noWrap>
                {toCurrency(
                  computeGrossPrice(product.basePrice, product.taxRate)
                )}
                {Number(product.taxRate || 0) > 0
                  ? ` (inkl. ${product.taxRate}% MwSt.)`
                  : ""}
                {product.description ? ` · ${product.description}` : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onEdit(product)}
            >
              Bearbeiten
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={deletingProduct}
              onClick={() => onDelete(product._id)}
            >
              Löschen
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Grid>
  );
}

// Simplified drag preview
function ProductDragPreview({ product }) {
  return (
    <Paper
      elevation={8}
      sx={{
        p: 1.5,
        borderRadius: 2,
        maxWidth: 300,
        opacity: 0.92,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {product.images?.[0] ? (
          <Box
            component="img"
            src={resolveProductImageUrl(product.images[0])}
            alt={product.name}
            sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
          />
        ) : null}
        <Typography variant="subtitle2" noWrap>
          {product.name}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function ShopProductsTab({
  products,
  loadingProducts,
  onOpenCreateDialog,
  onEdit,
  onDelete,
  onToggleActive,
  onReorder,
  deletingProduct,
  togglingId,
}) {
  const [activeDragProduct, setActiveDragProduct] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    })
  );

  const handleDragStart = useCallback((event) => {
    const product = event.active.data?.current?.product;
    setActiveDragProduct(product || null);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      setActiveDragProduct(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = products.findIndex(
        (p) => String(p._id) === String(active.id)
      );
      const newIndex = products.findIndex(
        (p) => String(p._id) === String(over.id)
      );
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

      const reordered = [...products];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      onReorder(reordered.map((p) => p._id));
    },
    [products, onReorder]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragProduct(null);
  }, []);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h6">Produktverwaltung</Typography>
          <Typography variant="body2" color="text.secondary">
            Produkte per Drag &amp; Drop neu anordnen. Die Reihenfolge wird im
            Katalog übernommen.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={onOpenCreateDialog}
          startIcon={<Iconify icon="eva:plus-fill" width={18} height={18} />}
        >
          Neues Produkt
        </Button>
      </Stack>

      {loadingProducts ? (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} md={6} key={`skel-${i}`}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Skeleton
                    variant="rectangular"
                    width={48}
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton width="60%" height={24} />
                    <Skeleton width="40%" height={18} />
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : products.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 5, textAlign: "center", borderRadius: 2 }}
        >
          <Iconify
            icon="eva:shopping-bag-outline"
            width={72}
            sx={{ color: "grey.300", mb: 2 }}
          />
          <Typography
            variant="h6"
            color="text.secondary"
            fontWeight={600}
            gutterBottom
          >
            Noch keine Produkte angelegt
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
            Erstelle dein erstes Produkt, um den Shop für deine Mitglieder zu
            aktivieren.
          </Typography>
          <Button
            variant="contained"
            onClick={onOpenCreateDialog}
            startIcon={<Iconify icon="eva:plus-fill" width={18} />}
          >
            Erstes Produkt erstellen
          </Button>
        </Paper>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Grid container spacing={2}>
            {products.map((product) => (
              <DraggableProductRow
                key={product._id}
                product={product}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleActive={onToggleActive}
                deletingProduct={deletingProduct}
                togglingId={togglingId}
              />
            ))}
          </Grid>
          <DragOverlay
            dropAnimation={{
              duration: 180,
              easing: "cubic-bezier(0.18,0.67,0.6,1.22)",
            }}
            zIndex={1800}
          >
            {activeDragProduct ? (
              <ProductDragPreview product={activeDragProduct} />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </Stack>
  );
}
